import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Search, Copy, ExternalLink, ChevronLeft, ChevronRight, X, Play, RefreshCw,
  Home, LayoutDashboard, ShoppingCart, Users, Mail, MessageCircle, BookOpen, Settings, Globe, LogIn,
} from "lucide-react";

/**
 * Full site map of the application organized by user journey.
 * Each entry: { title (ar), path, description, testid }.
 */
const SITEMAP = [
  {
    key: "public",
    label: "الصفحات العامة والتسجيل",
    icon: Globe,
    accent: "border-blue-200",
    items: [
      { title: "الصفحة الرئيسية / تحويل", path: "/", desc: "نقطة الدخول — تحوّل حسب حالة الجلسة" },
      { title: "تسجيل الدخول", path: "/login", desc: "شاشة تسجيل الدخول للأعضاء والإدارة" },
      { title: "نسيت كلمة المرور", path: "/forgot-password", desc: "طلب رابط إعادة تعيين" },
      { title: "إعادة تعيين كلمة المرور", path: "/reset-password?token=SAMPLE", desc: "شاشة تعيين كلمة جديدة (رمز عيّنة)" },
      { title: "تفعيل الحساب", path: "/activate?token=SAMPLE", desc: "استكمال التسجيل بعد الشراء" },
      { title: "إعادة تفعيل الاشتراك", path: "/reactivate", desc: "شاشة إعادة تفعيل بعد الإلغاء" },
      { title: "إلغاء الاشتراك من القوائم البريدية", path: "/unsubscribe", desc: "One-click unsubscribe عبر بريد" },
    ],
  },
  {
    key: "checkout",
    label: "تدفق الدفع (Checkout Flow)",
    icon: ShoppingCart,
    accent: "border-emerald-200",
    items: [
      { title: "صفحة الدفع", path: "/checkout", desc: "الصفحة الرئيسية للاشتراك في الخطط" },
      { title: "صفحة نجاح الدفع", path: "/checkout/success", desc: "تُعرض بعد إكمال Stripe checkout" },
    ],
  },
  {
    key: "member",
    label: "لوحة العضو (Member Area)",
    icon: LayoutDashboard,
    accent: "border-orange-200",
    items: [
      { title: "لوحة العضو الرئيسية", path: "/dashboard", desc: "نظرة عامة، آخر الدروس، إحصائيات شخصية" },
      { title: "قائمة الدورات", path: "/courses", desc: "جميع دورات العضو مع التقدم" },
      { title: "تفاصيل دورة", path: "/courses/sample-course-id", desc: "المشغّل + قائمة الدروس (يتم إعادة بنائها)" },
      { title: "قائمة الوصفات", path: "/recipes", desc: "بحث + فلاتر (نوع الوجبة، الحمية، المكوّنات)" },
      { title: "تفاصيل وصفة", path: "/recipes/sample-recipe-id", desc: "المكوّنات، الخطوات، القيم الغذائية" },
      { title: "المجتمع", path: "/community", desc: "منشورات، تعليقات، تفاعل، تعديل" },
      { title: "المحادثة الخاصة مع المدرب", path: "/chat", desc: "رسائل خاصة مباشرة" },
      { title: "الإعدادات الشخصية", path: "/settings", desc: "الملف الشخصي، الأمان، تفضيلات الإشعارات" },
    ],
  },
  {
    key: "admin_overview",
    label: "الإدارة — نظرة عامة",
    icon: LayoutDashboard,
    accent: "border-slate-200",
    items: [
      { title: "لوحة الإدارة الرئيسية", path: "/admin", desc: "KPIs، أحدث النشاط، اختصارات" },
      { title: "حالة النظام", path: "/admin/system", desc: "السعة، اتجاهات النمو، توصيات الترقية" },
      { title: "التدفقات التلقائية", path: "/admin/automated-flows", desc: "أتمتة بريد + واتساب بعد الشراء" },
      { title: "خريطة الموقع (هذه الصفحة)", path: "/admin/sitemap", desc: "معاينة حية لكل شاشة في المنصة" },
    ],
  },
  {
    key: "admin_members",
    label: "الإدارة — الأعضاء والعملاء",
    icon: Users,
    accent: "border-purple-200",
    items: [
      { title: "العملاء المحتملون (Leads)", path: "/admin/leads", desc: "قائمة كل الليدز + المصادر + المشتريات" },
      { title: "رحلة العميل", path: "/admin/journey?email=admin@faisalfit.com", desc: "الخط الزمني الموحّد لكل نقاط التماس" },
      { title: "الأعضاء المسجّلون", path: "/admin/members", desc: "CRUD، منح الوصول، إعادة تعيين كلمة المرور" },
      { title: "روابط التفعيل", path: "/admin/invites", desc: "جميع رموز التفعيل والحالة" },
    ],
  },
  {
    key: "admin_sales",
    label: "الإدارة — المبيعات والدفع",
    icon: ShoppingCart,
    accent: "border-green-200",
    items: [
      { title: "المشتريات", path: "/admin/purchases", desc: "كل عمليات الدفع مع فلاتر" },
      { title: "الاشتراكات (MRR, Churn)", path: "/admin/subscriptions", desc: "MRR، churn، cohorts، تنبيهات مخاطر" },
      { title: "تحليلات التسويق (Attribution)", path: "/admin/attribution", desc: "بريد مقابل واتساب، مصادر التحويل" },
      { title: "خطط الاشتراك", path: "/admin/plans", desc: "أسعار خطتي 6 أشهر و12 شهر + أسعار مخصصة لكل عملة" },
      { title: "أكواد الخصم", path: "/admin/coupons", desc: "CRUD + وسم القنوات + استنساخ لكل قناة" },
    ],
  },
  {
    key: "admin_email",
    label: "الإدارة — التسويق بالبريد",
    icon: Mail,
    accent: "border-amber-200",
    items: [
      { title: "صندوق الوارد (Inbound)", path: "/admin/inbox", desc: "ردود العملاء عبر Resend webhooks" },
      { title: "قوالب البريد", path: "/admin/emails/templates", desc: "قوالب Transactional قابلة للتعديل" },
      { title: "تسلسلات البريد", path: "/admin/emails/sequences", desc: "Drip campaigns متعددة الخطوات" },
      { title: "الحملات البريدية", path: "/admin/broadcasts", desc: "إرسال دفعة واحدة لجميع الشرائح" },
      { title: "سجل البريد", path: "/admin/emails/queue", desc: "كل الرسائل: مُجدولة، مُرسلة، فاشلة" },
    ],
  },
  {
    key: "admin_whatsapp",
    label: "الإدارة — واتساب",
    icon: MessageCircle,
    accent: "border-teal-200",
    items: [
      { title: "لوحة واتساب", path: "/admin/whatsapp", desc: "قوالب Twilio، التسلسلات، سجل التسليم" },
    ],
  },
  {
    key: "admin_content",
    label: "الإدارة — المحتوى والمجتمع",
    icon: BookOpen,
    accent: "border-rose-200",
    items: [
      { title: "إدارة الدورات", path: "/admin/courses", desc: "CRUD الدورات، رفع الفيديو، منح الوصول" },
      { title: "إدارة الوصفات", path: "/admin/recipes", desc: "CRUD الوصفات، الصور، الفلاتر" },
      { title: "إدارة المجتمع", path: "/admin/community", desc: "طابور الاعتماد، تثبيت، حذف" },
      { title: "الرسائل الخاصة", path: "/admin/chat", desc: "عرض جميع محادثات المدرب" },
    ],
  },
  {
    key: "admin_system",
    label: "الإدارة — النظام",
    icon: Settings,
    accent: "border-gray-200",
    items: [
      { title: "سجل Webhooks", path: "/admin/webhooks", desc: "Stripe / Twilio / Resend — كل الأحداث الواردة" },
      { title: "فحص الاتصالات", path: "/admin/diagnostics", desc: "اختبارات حية للخدمات الخارجية" },
    ],
  },
];

// Flatten all items in reading order for slider navigation
const FLAT_ITEMS = SITEMAP.flatMap((g) => g.items.map((it) => ({ ...it, groupLabel: g.label, groupKey: g.key })));

export default function AdminSiteMap() {
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(null); // slider mode when not null
  const [iframeKey, setIframeKey] = useState(0);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return SITEMAP;
    const q = search.trim().toLowerCase();
    return SITEMAP
      .map((g) => ({ ...g, items: g.items.filter((i) => i.title.toLowerCase().includes(q) || i.path.toLowerCase().includes(q) || (i.desc || "").toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [search]);

  const openSliderAtPath = useCallback((path) => {
    const idx = FLAT_ITEMS.findIndex((i) => i.path === path);
    if (idx >= 0) setActiveIndex(idx);
  }, []);

  const closeSlider = useCallback(() => setActiveIndex(null), []);
  const goNext = useCallback(() => setActiveIndex((i) => (i == null ? null : (i + 1) % FLAT_ITEMS.length)), []);
  const goPrev = useCallback(() => setActiveIndex((i) => (i == null ? null : (i - 1 + FLAT_ITEMS.length) % FLAT_ITEMS.length)), []);

  useEffect(() => {
    if (activeIndex == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeSlider();
      else if (e.key === "ArrowLeft") goPrev();      // RTL: left → next
      else if (e.key === "ArrowRight") goNext();     // RTL: right → prev... actually in RTL people expect ArrowRight = previous, ArrowLeft = next
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeIndex, closeSlider, goNext, goPrev]);

  const copyUrl = async (path) => {
    const full = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(full);
      toast.success("تم نسخ الرابط.");
    } catch {
      toast.error("تعذّر النسخ.");
    }
  };

  const current = activeIndex != null ? FLAT_ITEMS[activeIndex] : null;

  return (
    <div className="space-y-4" dir="rtl" data-testid="admin-sitemap">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-semibold">خريطة الموقع — معاينة حيّة</h1>
          <p className="text-foreground/60 mt-1 text-sm">
            كل شاشة في المنصة معروضة كبطاقة مصغّرة مع رابطها. اضغط على أي بطاقة للفتح في وضع الشرائح مع أسهم للتنقّل.
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute end-3 top-1/2 -translate-y-1/2 text-foreground/40" />
          <Input
            placeholder="ابحث في الصفحات..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pe-9"
            data-testid="sitemap-search"
          />
        </div>
      </div>

      {filteredGroups.map((g) => {
        const GroupIcon = g.icon || Home;
        return (
          <section key={g.key} data-testid={`sitemap-group-${g.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <GroupIcon className="h-4 w-4 text-foreground/60" />
              <h2 className="font-semibold text-base">{g.label}</h2>
              <span className="text-[10px] text-foreground/40">({g.items.length})</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {g.items.map((it) => (
                <PreviewCard
                  key={it.path}
                  item={it}
                  accent={g.accent}
                  onOpen={() => openSliderAtPath(it.path)}
                  onCopy={() => copyUrl(it.path)}
                />
              ))}
            </div>
          </section>
        );
      })}

      {filteredGroups.length === 0 && (
        <div className="text-center text-foreground/50 text-sm py-16">لا توجد نتائج مطابقة.</div>
      )}

      {current && (
        <SliderModal
          key={activeIndex}
          item={current}
          index={activeIndex}
          total={FLAT_ITEMS.length}
          onClose={closeSlider}
          onNext={goNext}
          onPrev={goPrev}
          iframeKey={iframeKey}
          refresh={() => setIframeKey((k) => k + 1)}
          copy={() => copyUrl(current.path)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function PreviewCard({ item, accent, onOpen, onCopy }) {
  return (
    <Card className={`overflow-hidden border ${accent} hover:shadow-md transition-shadow group`} data-testid={`sitemap-card-${item.path}`}>
      <button
        onClick={onOpen}
        className="w-full block relative bg-foreground/5"
        style={{ height: 160 }}
        aria-label={`فتح معاينة ${item.title}`}
      >
        {/* Scaled iframe (pointer-events disabled so clicks bubble to button) */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden bg-white">
          <iframe
            src={item.path}
            title={item.title}
            className="border-0"
            style={{
              width: "1400px",
              height: "900px",
              transform: "scale(0.22)",
              transformOrigin: "top right",
              // RTL: origin at top-right so scaled iframe aligns with the RTL card
            }}
            loading="lazy"
            sandbox="allow-same-origin allow-scripts allow-forms"
          />
        </div>
        <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-foreground/25 transition-colors">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-2 shadow">
            <Play className="h-5 w-5 text-foreground" />
          </div>
        </div>
      </button>
      <div className="p-3 space-y-1">
        <div className="font-semibold text-sm truncate" title={item.title}>{item.title}</div>
        {item.desc && <div className="text-[11px] text-foreground/50 line-clamp-2 min-h-[28px]">{item.desc}</div>}
        <div className="flex items-center gap-1 pt-1">
          <code className="flex-1 text-[10px] font-mono bg-foreground/5 rounded px-2 py-1 truncate" dir="ltr" title={item.path}>{item.path}</code>
          <Button size="icon" variant="ghost" onClick={onCopy} className="h-7 w-7" data-testid={`sitemap-copy-${item.path}`} title="نسخ الرابط">
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <a href={item.path} target="_blank" rel="noreferrer" className="h-7 w-7 grid place-items-center rounded hover:bg-foreground/5" title="فتح في تبويب جديد" data-testid={`sitemap-open-${item.path}`}>
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function SliderModal({ item, index, total, onClose, onNext, onPrev, iframeKey, refresh, copy }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="sitemap-slider">
      {/* Header */}
      <div className="absolute top-0 inset-x-0 p-3 flex items-center justify-between text-white/90" dir="rtl">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs opacity-70">{index + 1} / {total}</span>
          <span className="text-xs opacity-50">·</span>
          <span className="text-xs opacity-70 truncate">{item.groupLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={refresh} data-testid="slider-refresh">
            <RefreshCw className="h-4 w-4 me-1" />تحديث
          </Button>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={copy} data-testid="slider-copy">
            <Copy className="h-4 w-4 me-1" />نسخ الرابط
          </Button>
          <a
            href={item.path}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center text-sm text-white hover:bg-white/10 px-3 py-1.5 rounded"
            data-testid="slider-open"
          >
            <ExternalLink className="h-4 w-4 me-1" />فتح في تبويب
          </a>
          <button onClick={onClose} className="p-2 rounded hover:bg-white/10" data-testid="slider-close" aria-label="إغلاق">
            <X className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Prev / Next arrows (RTL-aware: right arrow = previous, left arrow = next) */}
      <button
        onClick={onPrev}
        className="absolute end-4 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-white/10 hover:bg-white/25 text-white grid place-items-center backdrop-blur transition-colors"
        aria-label="السابق"
        data-testid="slider-prev"
      >
        <ChevronRight className="h-6 w-6" />
      </button>
      <button
        onClick={onNext}
        className="absolute start-4 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-white/10 hover:bg-white/25 text-white grid place-items-center backdrop-blur transition-colors"
        aria-label="التالي"
        data-testid="slider-next"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      {/* Preview card */}
      <div className="w-full max-w-6xl h-[85vh] bg-white rounded-lg overflow-hidden shadow-2xl flex flex-col mt-8" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 border-b bg-gradient-to-l from-orange-50 to-white flex items-center justify-between gap-3" dir="rtl">
          <div className="min-w-0">
            <div className="font-semibold text-base truncate">{item.title}</div>
            {item.desc && <div className="text-xs text-foreground/60 truncate">{item.desc}</div>}
          </div>
          <code className="text-xs font-mono bg-foreground/5 rounded px-2 py-1 truncate max-w-md" dir="ltr">{item.path}</code>
        </div>
        <iframe
          key={iframeKey}
          src={item.path}
          title={item.title}
          className="flex-1 w-full border-0 bg-white"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
          data-testid="slider-iframe"
        />
      </div>

      {/* Backdrop click to close */}
      <button onClick={onClose} className="absolute inset-0 -z-10" aria-hidden />
    </div>
  );
}
